import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Spin, Alert, Modal, Table, Tag, Segmented, Empty,
  Button, Radio, AutoComplete, DatePicker, message,
} from "antd";
import dayjs from "dayjs";
import { GiftOutlined } from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import customClientAxios from "../../customAxios";
import {
  PlatformAnalyticsSaasPlanStats,
  PlatformAnalyticsSaasPlanDrilldown,
  PlatformAnalyticsSaasPlanSearch,
  PlatformAnalyticsSaasPlanGrantTrial,
} from "../../utils/ApiEndpoints";
import {
  PageContainer, Card, SectionTitle, StatsGrid, StatCard,
  StatLabel, StatValue, StatSub, fmtNum,
} from "../paUtils";
import { getGatewaySub } from "../../utils/utils";
import styled from "styled-components";

// ── Plan visual config ────────────────────────────────────────────────────

const PLAN_META = {
  free:     { label: "Free",     color: "#94a3b8", description: "Default tier — no paid features unlocked" },
  growth:   { label: "Growth",   color: "#10b981", description: "Instagram CRM, Lead Pages, Grow tools, WhatsApp Blast" },
  pro:      { label: "Pro",      color: "#6366f1", description: "Potential Customers, WhatsApp Click Insights" },
  advanced: { label: "Advanced", color: "#a855f7", description: "Top tier — all features included" },
};

const PLAN_ORDER = ["free", "growth", "pro", "advanced"];

// ── Styled ────────────────────────────────────────────────────────────────

const ClickableStatCard = styled(StatCard)`
  cursor: pointer;
  border-left: 4px solid ${(p) => p.$color || "#cbd5e1"};
  &:hover {
    border-color: ${(p) => p.$color || "#cbd5e1"};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  font-size: 13px;
`;

// ── Tooltip ───────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>{label}</div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            margin: "4px 0", color: "#374151",
          }}
        >
          <span style={{
            display: "inline-block", width: 8, height: 8,
            borderRadius: "50%", background: entry.color,
          }} />
          <span style={{ color: "#64748b", minWidth: 80, textTransform: "capitalize" }}>
            {entry.name}:
          </span>
          <span style={{ fontWeight: 600 }}>{fmtNum(entry.value)}</span>
        </div>
      ))}
    </TooltipBox>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

const STATUS_COLOR = {
  active: "green",
  created: "gold",
  authenticated: "blue",
  pending: "orange",
  halted: "red",
  cancelled: "red",
  completed: "default",
  expired: "red",
  paused: "default",
};

// ── Drilldown Modal ───────────────────────────────────────────────────────

const DRILL_COLUMNS = [
  {
    title: "Owner",
    key: "owner",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
          {r.ownerName || "—"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.ownerEmail || ""}</div>
      </div>
    ),
  },
  {
    title: "Brand Page",
    key: "bp",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 500, color: "#1e293b", fontSize: 13 }}>{r.name || "—"}</div>
        {r.slug && <div style={{ fontSize: 11, color: "#94a3b8" }}>/{r.slug}</div>}
      </div>
    ),
  },
  {
    title: "Phone",
    dataIndex: "ownerPhone",
    key: "ownerPhone",
    width: 140,
    render: (v) => v || "—",
  },
  {
    title: "Status",
    key: "status",
    width: 110,
    render: (_, r) => {
      const s = getGatewaySub(r)?.status;
      if (!s) return <Tag style={{ fontSize: 11 }}>none</Tag>;
      return (
        <Tag color={STATUS_COLOR[s] || "default"} style={{ fontSize: 11 }}>
          {s}
        </Tag>
      );
    },
  },
  {
    title: "Plan Started",
    dataIndex: "planStart",
    key: "planStart",
    width: 130,
    render: fmtDate,
  },
  {
    title: "Period End",
    key: "periodEnd",
    width: 130,
    // Period truth now comes from SubscriptionTransaction (projected as `periodEnd` by backend).
    render: (_, r) => fmtDate(r.periodEnd),
  },
];

function DrilldownModal({ open, plan, startDate, endDate, onClose }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const meta = plan ? PLAN_META[plan] : null;

  const fetchRows = useCallback(async () => {
    if (!plan || !open) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsSaasPlanDrilldown, {
        params: { plan, startDate, endDate, page, limit: 50 },
      });
      setRows(res.data.data.rows || []);
      setTotal(res.data.data.pagination?.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load drilldown");
    } finally {
      setLoading(false);
    }
  }, [plan, open, startDate, endDate, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [plan, startDate, endDate]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      title={
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                display: "inline-block", width: 12, height: 12,
                borderRadius: "50%", background: meta?.color || "#cbd5e1",
              }}
            />
            <span style={{ fontWeight: 600 }}>
              {meta?.label || plan} Plan — Clients
            </span>
            <Tag>{total} clients</Tag>
          </div>
          {meta?.description && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              {meta.description}
            </div>
          )}
        </div>
      }
    >
      {err && <Alert type="error" message={err} style={{ marginBottom: 12 }} />}
      <Spin spinning={loading}>
        <Table
          dataSource={rows}
          columns={DRILL_COLUMNS}
          rowKey="brandPageId"
          size="middle"
          pagination={{
            current: page,
            pageSize: 50,
            total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `${t} clients`,
          }}
          locale={{ emptyText: <Empty description="No clients on this plan in the selected range" /> }}
          scroll={{ x: 900 }}
        />
      </Spin>
    </Modal>
  );
}

// ── Grant Trial Modal ─────────────────────────────────────────────────────

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function GrantTrialModal({ open, onClose, onGranted }) {
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 300);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null); // brand page row
  const [plan, setPlan] = useState("pro");
  const [startDate, setStartDate] = useState(() => dayjs().startOf("day"));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch(""); setResults([]); setSelected(null); setPlan("pro");
      setStartDate(dayjs().startOf("day"));
      setSearchError(null);
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (debounced.trim().length < 2) { setResults([]); setSearchError(null); return; }
    setSearching(true);
    setSearchError(null);
    customClientAxios()
      .get(PlatformAnalyticsSaasPlanSearch, { params: { q: debounced } })
      .then((res) => { if (!cancelled) setResults(res.data?.data?.rows || []); })
      .catch((e) => {
        if (cancelled) return;
        setResults([]);
        const status = e?.response?.status;
        const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message;
        setSearchError(status ? `Search failed (${status}): ${msg}` : `Search failed: ${msg || "network error"}`);
      })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debounced]);

  const options = useMemo(
    () =>
      results.map((r) => ({
        value: r._id,
        label: (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, color: "#1e293b" }}>{r.name || "(no name)"}</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>/{r.slug}</div>
            </div>
            <Tag color={PLAN_META[r.plan]?.color || "default"} style={{ alignSelf: "center" }}>
              {r.plan}
            </Tag>
          </div>
        ),
        row: r,
      })),
    [results]
  );

  const handlePick = (value, option) => {
    setSelected(option.row);
    setSearch(option.row.name || option.row.slug || value);
  };

  const handleGrant = async () => {
    if (!selected || !startDate) return;
    setSubmitting(true);
    try {
      const res = await customClientAxios().post(
        PlatformAnalyticsSaasPlanGrantTrial,
        {
          brandPageId: selected._id,
          plan,
          startDate: startDate.toISOString(),
        }
      );
      const d = res.data?.data;
      message.success(
        `Granted ${plan} trial to ${d?.name || selected.name} until ${fmtDate(d?.trialExpiresAt)}`
      );
      onGranted?.();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.error || e?.response?.data?.message || "Failed to grant trial");
    } finally {
      setSubmitting(false);
    }
  };

  const endDate = startDate ? startDate.add(1, "month") : null;

  const selectedSubStatus = selected ? getGatewaySub(selected)?.status : null;
  // Best-effort client-side check; backend `grantSaasPlanTrial` re-validates against
  // the SubscriptionTransaction source of truth before writing.
  const isPaidCustomer = selected
    ? ["growth", "pro", "advanced"].includes(selected.plan) &&
      selectedSubStatus === "active"
    : false;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Grant Free Trial"
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>Cancel</Button>,
        <Button
          key="grant"
          type="primary"
          loading={submitting}
          disabled={!selected || !startDate || isPaidCustomer}
          onClick={handleGrant}
        >
          Grant 1-month trial
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 14, fontSize: 12, color: "#64748b" }}>
        Inserts a 1-month trial subscription record for the picked tier, starting
        on the selected date. Refuses if the brand page is already on a paid plan.
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>Brand page</div>
        <AutoComplete
          style={{ width: "100%" }}
          value={search}
          options={options}
          onSearch={(v) => { setSearch(v); setSelected(null); }}
          onChange={(v) => { setSearch(v); if (!v) setSelected(null); }}
          onSelect={handlePick}
          placeholder="Search by brand name or slug (min 2 chars)"
          notFoundContent={
            searching ? <Spin size="small" /> :
            debounced.trim().length < 2 ? "Type to search" : "No matches"
          }
        />
        {searchError && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 8 }}
            message={searchError}
          />
        )}
      </div>

      {selected && (
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: 12,
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600 }}>{selected.name || "(no name)"}</div>
          <div style={{ color: "#64748b", fontSize: 12 }}>/{selected.slug}</div>
          <div style={{ marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <span>
              Current plan:{" "}
              <Tag color={PLAN_META[selected.plan]?.color || "default"}>{selected.plan}</Tag>
            </span>
            {selectedSubStatus && (
              <span>
                Sub status: <Tag color={STATUS_COLOR[selectedSubStatus] || "default"}>
                  {selectedSubStatus}
                </Tag>
              </span>
            )}
          </div>
          {isPaidCustomer && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="This brand page is on a paid plan. Trial cannot be granted."
            />
          )}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>Trial plan</div>
        <Radio.Group value={plan} onChange={(e) => setPlan(e.target.value)}>
          <Radio.Button value="growth">Growth</Radio.Button>
          <Radio.Button value="pro">Pro</Radio.Button>
          <Radio.Button value="advanced">Advanced</Radio.Button>
        </Radio.Group>
      </div>

      <div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>Trial start date</div>
        <DatePicker
          value={startDate}
          onChange={(d) => setStartDate(d)}
          allowClear={false}
          format="DD MMM YYYY"
          style={{ width: 200 }}
        />
        {endDate && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
            Trial ends:{" "}
            <strong style={{ color: "#0f172a" }}>{endDate.format("DD MMM YYYY")}</strong>{" "}
            <span style={{ color: "#94a3b8" }}>(start + 1 month)</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Main Tab ──────────────────────────────────────────────────────────────

function SaasPlansTab({ startDate, endDate }) {
  const [period, setPeriod] = useState("daily");
  const [data, setData] = useState({ totals: {}, timeSeries: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drillPlan, setDrillPlan] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsSaasPlanStats, {
        params: { startDate, endDate, period },
      });
      setData(res.data.data || { totals: {}, timeSeries: [] });
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load SaaS plan stats");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data.totals || {};
  const totalClients = PLAN_ORDER.reduce(
    (sum, p) => sum + (totals[p] || 0),
    0
  );

  return (
    <PageContainer>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 20 }}
               closable onClose={() => setError(null)} />
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<GiftOutlined />}
          onClick={() => setGrantOpen(true)}
        >
          Grant Trial
        </Button>
      </div>

      <Spin spinning={loading}>
        {/* Stat cards — click to drill down */}
        <StatsGrid>
          {PLAN_ORDER.map((plan) => {
            const meta = PLAN_META[plan];
            const count = totals[plan] || 0;
            const pct = totalClients > 0 ? ((count / totalClients) * 100).toFixed(1) : "0.0";
            return (
              <ClickableStatCard
                key={plan}
                $color={meta.color}
                onClick={() => setDrillPlan(plan)}
              >
                <StatLabel style={{ color: meta.color }}>{meta.label} Plan</StatLabel>
                <StatValue>{fmtNum(count)}</StatValue>
                <StatSub>{pct}% of all clients · click to view</StatSub>
              </ClickableStatCard>
            );
          })}
        </StatsGrid>

        {/* Time-series chart */}
        <Card>
          <HeaderRow>
            <SectionTitle style={{ margin: 0 }}>
              Plan Adoption Over Time
            </SectionTitle>
            <Segmented
              value={period}
              onChange={setPeriod}
              options={[
                { label: "Daily",   value: "daily" },
                { label: "Weekly",  value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
            />
          </HeaderRow>

          {data.timeSeries?.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data.timeSeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {PLAN_ORDER.map((plan) => (
                  <Bar
                    key={plan}
                    dataKey={plan}
                    name={PLAN_META[plan].label}
                    stackId="plans"
                    fill={PLAN_META[plan].color}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No data for the selected range" />
          )}
        </Card>
      </Spin>

      <DrilldownModal
        open={!!drillPlan}
        plan={drillPlan}
        startDate={startDate}
        endDate={endDate}
        onClose={() => setDrillPlan(null)}
      />

      <GrantTrialModal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onGranted={fetchData}
      />
    </PageContainer>
  );
}

export default SaasPlansTab;
